import { AVATAR_TYPES } from "../utils/avatar-utils";
import { registerComponentInstance, deregisterComponentInstance } from "../utils/component-utils";
import { MediaDevices, MediaDevicesEvents } from "../utils/media-devices-utils";
import { createHeadlessModelForSkinnedMesh } from "../utils/three-utils";
import { Layers } from "../camera-layers";
import configs from "../utils/configs";
import { getBundledDefaultAvatarUrl } from "../utils/default-avatar-url";
import { syncLocalPlayerInfoMutedState } from "./player-info-utils";
import { ensureNetworkedMicStateSync } from "../utils/networked-mic-state";
import { videoStreamUrlForClient } from "../utils/video-stream-url-utils";

function ensureAvatarNodes(json) {
  const { nodes, meshes } = json;

  // Find any node with a mesh that has the "mouthOpen" morph target
  const mouthMorphNode = nodes.find(node => {
    if (node.mesh === undefined) return false;
    const mesh = meshes[node.mesh];
    const targetNames = mesh?.extras?.targetNames || [];
    return targetNames.includes("mouthOpen");
  });

  if (mouthMorphNode) {
    mouthMorphNode.extensions = {
      MOZ_hubs_components: {
        "morph-audio-feedback": {
          name: "mouthOpen",
          minValue: 0,
          maxValue: 2
        }
      }
    };
  }

  if (!nodes.find(node => node.name === "Head")) {
    // If the avatar model doesn't have a Head node. The user has probably chosen a custom GLB.
    // So, we need to construct a suitable hierarchy for avatar functionality to work.
    // We re-parent the original root node to the Head node and set the scene root to a new AvatarRoot.

    // Note: We assume that the first node in the primary scene is the one we care about.
    const originalRoot = json.scenes[json.scene || 0].nodes[0];
    nodes.push({ name: "LeftEye", extensions: { MOZ_hubs_components: {} } });
    nodes.push({ name: "RightEye", extensions: { MOZ_hubs_components: {} } });
    nodes.push({
      name: "Head",
      children: [originalRoot, nodes.length - 1, nodes.length - 2],
      extensions: { MOZ_hubs_components: { "scale-audio-feedback": "" } }
    });
    nodes.push({ name: "Neck", children: [nodes.length - 1] });
    nodes.push({ name: "Spine", children: [nodes.length - 1] });
    nodes.push({ name: "Hips", children: [nodes.length - 1] });
    nodes.push({ name: "AvatarRoot", children: [nodes.length - 1] });
    json.scenes[json.scene || 0].nodes[0] = nodes.length - 1;
  }

  return json;
}

AFRAME.registerComponent("player-info", {
  schema: {
    avatarSrc: { type: "string" },
    avatarType: { type: "string", default: AVATAR_TYPES.SKINNABLE },
    muted: { default: false },
    isSharingAvatarCamera: { default: false }
  },
  init() {
    this.applyProperties = this.applyProperties.bind(this);
    this.handleModelError = this.handleModelError.bind(this);
    this.handleRemoteModelError = this.handleRemoteModelError.bind(this);
    this.update = this.update.bind(this);
    this.onPresenceUpdated = this.onPresenceUpdated.bind(this);
    this.onMicStateChanged = this.onMicStateChanged.bind(this);
    this.onAvatarModelLoaded = this.onAvatarModelLoaded.bind(this);
    this.releaseNetworkedMicStateSync = null;

    this.isLocalPlayerInfo = this.el.id === "avatar-rig";
    this.playerSessionId = null;

    if (!this.isLocalPlayerInfo) {
      NAF.utils.getNetworkedEntity(this.el).then(networkedEntity => {
        this.playerSessionId = NAF.utils.getCreator(networkedEntity);
        const playerPresence = window.APP.hubChannel.presence.state[this.playerSessionId];
        if (playerPresence) {
          this.permissions = playerPresence.metas[0].permissions;
        }
      });
    }

    registerComponentInstance(this, "player-info");
  },

  remove() {
    const avatarEl = this.el.querySelector("[avatar-audio-source]");
    APP.isAudioPaused.delete(avatarEl);
    deregisterComponentInstance(this, "player-info");
  },

  onAvatarModelLoaded(e) {
    this.applyProperties(e);

    const modelEl = this.el.querySelector(".model");
    if (this.isLocalPlayerInfo && e.target === modelEl) {
      let isSkinnedAvatar = false;
      modelEl.object3D.traverse(function (o) {
        if (o.isSkinnedMesh) {
          const headlessMesh = createHeadlessModelForSkinnedMesh(o);
          if (headlessMesh) {
            isSkinnedAvatar = true;
            o.parent.add(headlessMesh);
          }
        }
      });
      // This is to support using arbitrary models as avatars.
      // TODO We can drop support for this when we go full VRM, or at least handle it earlier in the process.
      if (!isSkinnedAvatar) {
        modelEl.object3D.traverse(function (o) {
          if (o.isMesh) o.layers.set(Layers.CAMERA_LAYER_THIRD_PERSON_ONLY);
        });
      }
    }

    // Enable shadow casting on avatar meshes if dynamic shadows are enabled
    this.updateAvatarShadows();
  },

  play() {
    this.el.addEventListener("model-loaded", this.onAvatarModelLoaded);
    this.el.sceneEl.addEventListener("presence_updated", this.onPresenceUpdated);
    if (this.isLocalPlayerInfo) {
      this.el.querySelector(".model").addEventListener("model-error", this.handleModelError);
    } else {
      this.el.querySelector(".model").addEventListener("model-error", this.handleRemoteModelError);
    }
    window.APP.store.addEventListener("statechanged", this.update);

    this.el.sceneEl.addEventListener("stateadded", this.update);
    this.el.sceneEl.addEventListener("stateremoved", this.update);

    if (this.isLocalPlayerInfo) {
      this.releaseNetworkedMicStateSync = ensureNetworkedMicStateSync();
      APP.dialog.on("mic-state-changed", this.onMicStateChanged);
      if (typeof APP.dialog?.isMicEnabled === "boolean") {
        syncLocalPlayerInfoMutedState(this.el, APP.dialog.isMicEnabled);
      }
    }
  },

  pause() {
    this.el.removeEventListener("model-loaded", this.onAvatarModelLoaded);
    this.el.sceneEl.removeEventListener("presence_updated", this.onPresenceUpdated);
    if (this.isLocalPlayerInfo) {
      this.el.querySelector(".model").removeEventListener("model-error", this.handleModelError);
    } else {
      this.el.querySelector(".model").removeEventListener("model-error", this.handleRemoteModelError);
    }
    this.el.sceneEl.removeEventListener("stateadded", this.update);
    this.el.sceneEl.removeEventListener("stateremoved", this.update);
    window.APP.store.removeEventListener("statechanged", this.update);

    if (this.isLocalPlayerInfo) {
      APP.dialog.off("mic-state-changed", this.onMicStateChanged);
      this.releaseNetworkedMicStateSync?.();
      this.releaseNetworkedMicStateSync = null;
    }
  },

  onPresenceUpdated(e) {
    this.updateFromPresenceMeta(e.detail);
  },

  updateFromPresenceMeta(presenceMeta) {
    if (!this.playerSessionId && this.isLocalPlayerInfo) {
      this.playerSessionId = NAF.clientId;
    }
    if (!this.playerSessionId || this.playerSessionId !== presenceMeta.sessionId) return;

    this.permissions = presenceMeta.permissions;
  },

  update(oldData) {
    if (this.data.muted !== oldData.muted) {
      this.el.emit("remote_mute_updated", { muted: this.data.muted });
      this.el.sceneEl?.emit("mic_presence_updated", {
        sessionId: this.playerSessionId,
        muted: this.data.muted
      });
    }
    this.applyProperties();
    this.updateAvatarShadows();
  },

  can(perm) {
    return !!this.permissions && this.permissions[perm];
  },

  applyProperties(e) {
    const modelEl = this.el.querySelector(".model");
    if (this.data.avatarSrc && modelEl) {
      modelEl.components["gltf-model-plus"].jsonPreprocessor = ensureAvatarNodes;
      modelEl.setAttribute("gltf-model-plus", "src", this.data.avatarSrc);
    }

    if (!e || e.target === modelEl) {
      // const uniforms = injectCustomShaderChunks(this.el.object3D);
      // this.el.querySelectorAll("[hover-visuals]").forEach(el => {
      //   el.components["hover-visuals"].uniforms = uniforms;
      // });
    }

    const videoTextureTargets = modelEl?.querySelectorAll("[video-texture-target]");

    const sessionId = this.isLocalPlayerInfo ? NAF.clientId : this.playerSessionId;

    for (const el of Array.from(videoTextureTargets)) {
      el.setAttribute("video-texture-target", {
        src: this.data.isSharingAvatarCamera ? videoStreamUrlForClient(sessionId, MediaDevices.CAMERA) : ""
      });

      if (this.isLocalPlayerInfo) {
        el.setAttribute(
          "emit-scene-event-on-remove",
          `event:${MediaDevicesEvents.VIDEO_SHARE_ENDED}; source:${MediaDevices.CAMERA}`
        );
      }
    }

    const avatarEl = this.el.querySelector("[avatar-audio-source]");
    if (this.data.muted) {
      APP.isAudioPaused.add(avatarEl);
    } else {
      APP.isAudioPaused.delete(avatarEl);
    }
  },

  handleModelError() {
    window.APP.store.resetToRandomDefaultAvatar();
  },

  handleRemoteModelError() {
    this.data.avatarSrc = getBundledDefaultAvatarUrl(configs.BASE_ASSETS_PATH);
    this.applyProperties();
  },

  onMicStateChanged({ enabled }) {
    syncLocalPlayerInfoMutedState(this.el, enabled);
  },

  updateAvatarShadows() {
    const modelEl = this.el.querySelector(".model");
    if (!modelEl) return;
    const shouldCast = !this.isLocalPlayerInfo && window.APP.store.state.preferences.enableDynamicShadows === true;
    modelEl.object3D.traverse(child => {
      if (child.isMesh) {
        child.castShadow = shouldCast;
      }
    });
  }
});
